// @flow
import {
  ActionCreator,
  buildCatchError,
  createReducer,
} from '../../../store/utils';
import {ofType} from 'redux-observable';
import {map, switchMap, debounceTime} from 'rxjs/operators';
import {buildRoute} from '../../../http';
import {
  endpoints,
  getPendingReviewsApi,
  editReviewApi,
  getReviewsApi,
  getReviewAverageApi,
  sendEmailApi,
} from './api';

import type {ActionsObservable} from 'redux-observable';
import type {Action} from '../../../store/utils';
import type {Review, Order, Pagination} from '../../../entities';
import type {ApiError} from '../../../http/error';

export type ReviewsAction = Action & {
  reviews?: Review[],
  review?: Review,
  enabled?: boolean,
  featured?: boolean,
  orderId?: string,
  pendingReviews?: Order[],
  paginationReviews?: Pagination,
  queryFilter?: {[key: string]: string},
  error?: string | ApiError,
};

export type ReviewsState = {
  isSavingReview?: boolean,
  saveReviewSuccess?: boolean,
  isFetchingReviews?: boolean,
  isFetchingPendingReviews?: boolean,
  pendingReviews?: Order[],
  reviews?: Review[],
  error?: string,
  paginationReviewsPending?: Pagination,
  paginationReviews?: Pagination,
  average?: string,
  sendEmailSuccess?: boolean,
  isSendingEmail?: boolean,
  isFetchingAverage?: boolean,
  enableReviewSuccess?: boolean,
  disableReviewSuccess?: boolean,
};

export class ResetState extends ActionCreator<ReviewsAction> {}
export class ReviewNoop extends ActionCreator<ReviewsAction> {}

export class FetchPendingReviews extends ActionCreator<ReviewsAction> {}
export class FetchPendingReviewsSuccess extends ActionCreator<ReviewsAction> {}
export class FetchPendingReviewsErrorFn extends ActionCreator<ReviewsAction> {}

export class FetchReviews extends ActionCreator<ReviewsAction> {}
export class FetchReviewsSuccess extends ActionCreator<ReviewsAction> {}
export class FetchReviewsErrorFn extends ActionCreator<ReviewsAction> {}

export class EnableReview extends ActionCreator<ReviewsAction> {}
export class EnableReviewSuccess extends ActionCreator<ReviewsAction> {}
export class EnableReviewErrorFn extends ActionCreator<ReviewsAction> {}

export class FetchAverage extends ActionCreator<ReviewsAction> {}
export class FetchAverageSuccess extends ActionCreator<ReviewsAction> {}
export class FetchAverageErrorFn extends ActionCreator<ReviewsAction> {}

export class SendEmailReview extends ActionCreator<ReviewsAction> {}
export class SendEmailReviewSuccess extends ActionCreator<ReviewsAction> {}
export class SendEmailReviewErrorFn extends ActionCreator<ReviewsAction> {}

export const actions = {
  resetState: () => new ResetState(),
  fetchPendingReviews: (payload: ReviewsAction) =>
    new FetchPendingReviews(payload),
  fetchReviews: (payload: ReviewsAction) => new FetchReviews(payload),
  fetchAverage: () => new FetchAverage(),
  enableReview: (payload: ReviewsAction) => new EnableReview(payload),
  sendEmail: (payload: ReviewsAction) => new SendEmailReview(payload),
};
export const initialState: ReviewsState = {};

export default createReducer<ReviewsState>(initialState, {
  [ReviewNoop.type]: (state: ReviewsState) => state,
  [ResetState.type]: () => initialState,
  [FetchPendingReviews.type]: (state: ReviewsState) => ({
    ...state,
    isFetchingPendingReviews: true,
  }),
  [FetchPendingReviewsSuccess.type]: (
    state: ReviewsState,
    action: ReviewsAction
  ) => ({
    ...state,
    isFetchingPendingReviews: false,
    error: undefined,
    pendingReviews: action.data,
    paginationReviewsPending: action.pagination,
  }),
  [FetchPendingReviewsErrorFn.type]: (
    state: ReviewsState,
    action: ReviewsAction
  ) => ({
    ...state,
    isFetchingPendingReviews: false,
    error: action.error,
  }),
  [FetchReviews.type]: (state: ReviewsState) => ({
    ...state,
    isFetchingReviews: true,
  }),
  [FetchReviewsSuccess.type]: (state: ReviewsState, action: ReviewsAction) => ({
    ...state,
    isFetchingReviews: false,
    error: undefined,
    reviews: action.data,
    paginationReviews: action.pagination,
  }),
  [FetchReviewsErrorFn.type]: (state: ReviewsState, action: ReviewsAction) => ({
    ...state,
    isFetchingReviews: false,
    error: action.error,
  }),
  [EnableReview.type]: (state: ReviewsState, action: ReviewsAction) => {
    let auxState = {
      ...state,
      isUpdatingReview: true,
    };
    if (
      (typeof action.enabled !== 'undefined' ||
        typeof action.featured !== 'undefined') &&
      action.orderId &&
      state.reviews
    ) {
      const auxReviews = state.reviews.map(review => ({
        ...review,
        featured:
          typeof action.featured !== 'undefined' &&
          review.order.id === action.orderId
            ? action.featured
            : review.featured,
        enabled:
          typeof action.enabled !== 'undefined' &&
          review.order.id === action.orderId
            ? action.enabled
            : review.enabled,
      }));
      auxState.reviews = auxReviews;
    }
    return auxState;
  },
  [EnableReviewSuccess.type]: (state: ReviewsState, action: ReviewsAction) => {
    const aux = [...(state.reviews || [])];
    const index = aux.findIndex(c => c.order.id === action.review.order.id);
    if (index > -1) {
      aux[index] = action.review;
    }
    return {
      ...state,
      reviews: aux,
      isUpdatingReview: false,
      enableProductSuccess:
        typeof action.enabled === 'boolean' ? !!action.enabled : undefined,
      disableProductSuccess:
        typeof action.enabled === 'boolean' ? !action.enabled : undefined,
      error: undefined,
    };
  },
  [EnableReviewErrorFn.type]: (state: ReviewsState, action: ReviewsAction) => ({
    ...state,
    isUpdatingReview: false,
    error: action.error,
  }),
  [FetchAverage.type]: (state: ReviewsState) => ({
    ...state,
    isFetchingAverage: true,
  }),
  [FetchAverageSuccess.type]: (state: ReviewsState, action: ReviewsAction) => ({
    ...state,
    isFetchingAverage: false,
    error: undefined,
    average: action.average.average,
  }),
  [FetchAverageErrorFn.type]: (state: ReviewsState, action: ReviewsAction) => ({
    ...state,
    isFetchingAverage: false,
    error: action.error,
  }),
  [SendEmailReview.type]: (state: ReviewsState) => ({
    ...state,
    isSendingEmail: true,
  }),
  [SendEmailReviewSuccess.type]: (
    state: ReviewsState,
    action: ReviewsAction
  ) => ({
    ...state,
    isSendingEmail: false,
    error: undefined,
    sendEmailSuccess: true,
  }),
  [SendEmailReviewErrorFn.type]: (
    state: ReviewsState,
    action: ReviewsAction
  ) => ({
    ...state,
    isSendingEmail: false,
    error: action.error,
  }),
});

export const fetchPendingReviewsEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(FetchPendingReviews.type),
    debounceTime(250),
    switchMap(({queryFilter}: ReviewsAction) => {
      return getPendingReviewsApi({...queryFilter, reviewed: '0'}).pipe(
        map(
          ({data, pagination}: {data: Order[], pagination: Pagination}) =>
            new FetchPendingReviewsSuccess({data, pagination})
        ),
        buildCatchError(
          FetchPendingReviewsErrorFn,
          'Error al obtener las reseñas'
        )
      );
    })
  );

export const fetchReviewsEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(FetchReviews.type),
    debounceTime(250),
    switchMap(({queryFilter}: ReviewsAction) =>
      getReviewsApi({...queryFilter}).pipe(
        map(
          ({data, pagination}: {data: Review[], pagination: Pagination}) =>
            new FetchReviewsSuccess({data, pagination})
        ),
        buildCatchError(FetchReviewsErrorFn, 'Error al obtener las reseñas')
      )
    )
  );

export const enableReviewEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(EnableReview.type),
    switchMap(({enabled, featured, orderId}: ReviewsAction) =>
      editReviewApi(
        {enabled: enabled, featured: featured},
        {
          route: buildRoute(endpoints.EDIT_REVIEW, [orderId]),
        }
      ).pipe(
        map(review => new EnableReviewSuccess({review})),
        buildCatchError(EnableReviewErrorFn, 'Error al actualizar la reseña')
      )
    )
  );

export const fetchAverageEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(FetchAverage.type),
    debounceTime(250),
    switchMap(() =>
      getReviewAverageApi().pipe(
        map(average => new FetchAverageSuccess({average})),
        buildCatchError(
          FetchAverageErrorFn,
          'Error al obtener el promedio de resumen de las reseñas'
        )
      )
    )
  );

export const sendEmailEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(SendEmailReview.type),
    debounceTime(250),
    switchMap(({orderId}) => {
      return sendEmailApi(undefined, {
        route: buildRoute(endpoints.SEND_EMAIL, [orderId]),
      }).pipe(
        map(() => new SendEmailReviewSuccess()),
        buildCatchError(SendEmailReviewErrorFn, 'Error al  enviar email')
      );
    })
  );
